import type {
  SkillCollectionListBySkillQuery_ProgramCurriculumCollectionsV1Resource_skillCollectionBySkillId_elements_courses_elements as CourseElement,
  SkillCollectionListBySkillQuery_ProgramCurriculumCollectionsV1Resource_skillCollectionBySkillId_elements_s12ns_elements as S12nElement,
} from './__generated__/SkillCollectionListBySkillQuery';
import type { ProductItem } from './generateFilterDropdownOptions';
import type { FilterKeys, FilterState } from './useProductFilterState';
import { filterKeys } from './useProductFilterState';

// This function loops though all our filters and add the result of the individual filter to a hash map if it is an active filter.
// After we simply return the combined boolean of all the filters using an every. E.G. didMatchLanguage && didMatchLevel && didMatchPartner
const filterProducts = (products: ProductItem[], filters: FilterState) => {
  const filterResults = {} as Record<FilterKeys, boolean>;
  if (
    !filters[filterKeys.LANGUAGE] &&
    !filters[filterKeys.LEVEL] &&
    !filters[filterKeys.SUBJECT] &&
    !filters[filterKeys.PARTNER] &&
    !filters[filterKeys.LEARNING_PRODUCT]
  ) {
    // If no active filters don't filter
    return products;
  }

  return products.filter((product) => {
    // Active filters get added to this hashmap
    // If it is not active there is no entry in this map

    // Filter by language
    if (filters[filterKeys.LANGUAGE]) {
      const languageFilter = filters[filterKeys.LANGUAGE];
      if (product.isCourse) {
        const course = product.product as CourseElement;
        const courseLanguages = [...(course.primaryLanguages || []), ...(course.subtitleLanguages || [])];
        filterResults[filterKeys.LANGUAGE] = courseLanguages.includes(languageFilter);
      } else {
        const s12n = product.product as S12nElement;
        const s12nLanguages = (s12n.courses?.elements || []).reduce((languages, course) => {
          languages.push(...(course?.primaryLanguages || []), ...(course?.subtitleLanguages || []));
          return languages;
        }, [] as string[]);
        filterResults[filterKeys.LANGUAGE] = s12nLanguages?.includes(languageFilter);
      }
    }

    // Filter by level
    if (filters[filterKeys.LEVEL]) {
      const levelFilter = filters[filterKeys.LEVEL];
      if (product.isCourse) {
        const course = product.product as CourseElement;
        filterResults[filterKeys.LEVEL] = course.level === levelFilter;
      } else {
        const s12n = product.product as S12nElement;
        filterResults[filterKeys.LEVEL] = s12n.metadata?.level === levelFilter;
      }
    }

    // Filter by partner
    if (filters[filterKeys.PARTNER]) {
      const partnerFilter = filters[filterKeys.PARTNER];
      const coursePartners = (product.product.partners?.elements || [])
        .filter((partner) => !!partner)
        .map((partner) => partner?.id);
      filterResults[filterKeys.PARTNER] = coursePartners?.includes(partnerFilter);
    }

    // Filter by learning product
    if (filters[filterKeys.LEARNING_PRODUCT]) {
      const learningProductFilter = filters[filterKeys.LEARNING_PRODUCT];
      if (product.isCourse) {
        const course = product.product as CourseElement;
        const courseType = course.courseTypeMetadata?.courseTypeMetadata?.__typename;
        filterResults[filterKeys.LEARNING_PRODUCT] = courseType === learningProductFilter;
      } else {
        const s12n = product.product as S12nElement;
        const s12nType = s12n.productVariant;
        filterResults[filterKeys.LEARNING_PRODUCT] = s12nType === learningProductFilter;
      }
    }

    // Filter by subject
    if (filters[filterKeys.SUBJECT]) {
      const subjectFilter = filters[filterKeys.SUBJECT];
      if (product.isCourse) {
        const course = product.product as CourseElement;
        const courseDomainIds = (course.domainTypes || []).map((domainType) => domainType.domainId);
        filterResults[filterKeys.SUBJECT] = courseDomainIds.includes(subjectFilter);
      } else {
        const s12n = product.product as S12nElement;
        const s12nDomains = (s12n.domainTypes || []).map((domainType) => domainType.domainId);
        filterResults[filterKeys.SUBJECT] = s12nDomains?.includes(subjectFilter);
      }
    }

    // For active filters ensure they are all truthy
    return Object.values(filterResults).every((result) => result);
  });
};
export default filterProducts;
